import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../AuthContext';
import { rewardsAPI, pointsAPI } from '../../api';
import { formatDate } from '../../utils';
import { Card, Button, Input, Modal, Badge, Spinner } from '../../components/ui';
import { Gift, Plus, Star, CheckCircle, XCircle } from 'lucide-react';

export const RewardsPage = () => {
  const { user } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', cost_points: 100, 
    inventory: '', eligibility_level: 1, requires_approval: true
  });

  const loadData = useCallback(async () => {
    try {
      const [rewardsRes, redemptionsRes] = await Promise.all([
        rewardsAPI.list(),
        rewardsAPI.getRedemptions()
      ]);
      setRewards(rewardsRes.data);
      setRedemptions(redemptionsRes.data);
      
      if (user?.role === 'client') {
        const balanceRes = await pointsAPI.getBalance();
        setBalance(balanceRes.data);
      }
    } catch (err) {
      console.error('Failed to load rewards:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateReward = async (e) => {
    e.preventDefault();
    try {
      await rewardsAPI.create({
        ...form,
        inventory: form.inventory ? parseInt(form.inventory) : null
      });
      setShowModal(false);
      setForm({ name: '', description: '', cost_points: 100, inventory: '', eligibility_level: 1, requires_approval: true });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create reward');
    }
  };

  const handleRedeem = async (rewardId) => {
    if (!window.confirm('Are you sure you want to redeem this reward?')) return;
    try {
      await rewardsAPI.redeem(rewardId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to redeem reward');
    }
  };

  const handleProcessRedemption = async (redemptionId, approved) => {
    try {
      await rewardsAPI.processRedemption(redemptionId, approved, '');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to process redemption');
    }
  };

  const isAdmin = user?.role === 'admin';
  const pendingRedemptions = redemptions.filter(r => r.status === 'pending');

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="rewards-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Rewards</h1>
          <p className="text-gray-500">
            {isAdmin ? 'Manage reward catalog' : 'Redeem your points for rewards'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowModal(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add Reward
          </Button>
        )}
      </div>

      {/* Client Balance */}
      {balance && (
        <Card className="p-4 bg-primary-50 border-primary-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-primary-700">Available Points</p>
              <p className="text-2xl font-bold text-primary-900">{balance.total_points?.toLocaleString()}</p>
            </div>
            <Star className="w-8 h-8 text-primary-400" />
          </div>
        </Card>
      )}

      {/* Pending Redemptions (Admin) */}
      {isAdmin && pendingRedemptions.length > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <h3 className="font-semibold text-amber-900 mb-3">Pending Redemptions ({pendingRedemptions.length})</h3>
          <div className="space-y-3">
            {pendingRedemptions.map(r => (
              <div key={r.id} className="bg-white p-4 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium">{r.reward_name}</p>
                  <p className="text-sm text-gray-500">by {r.user_name} • {r.points_spent} pts</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleProcessRedemption(r.id, true)}>
                    <CheckCircle className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => handleProcessRedemption(r.id, false)}>
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Rewards Catalog */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rewards.length === 0 ? (
          <Card className="col-span-full p-8 text-center">
            <Gift className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No rewards available yet</p>
          </Card>
        ) : (
          rewards.map(reward => {
            const canAfford = balance ? balance.total_points >= reward.cost_points : false;
            const meetsLevel = balance ? balance.current_level >= reward.eligibility_level : true;
            
            return (
              <Card key={reward.id} className="overflow-hidden">
                <div className="h-32 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Gift className="w-12 h-12 text-white/80" />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold">{reward.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{reward.description}</p>
                  <div className="flex items-center justify-between mt-4">
                    <div>
                      <p className="text-lg font-bold text-primary-600">{reward.cost_points} pts</p>
                      {reward.inventory_remaining !== null && (
                        <p className="text-xs text-gray-400">{reward.inventory_remaining} left</p>
                      )}
                    </div>
                    {user?.role === 'client' && (
                      <Button 
                        size="sm" 
                        disabled={!canAfford || !meetsLevel}
                        onClick={() => handleRedeem(reward.id)}
                      >
                        Redeem
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      {/* My Redemptions (Client) */}
      {user?.role === 'client' && redemptions.length > 0 && (
        <Card>
          <div className="p-4 border-b">
            <h3 className="font-semibold">My Redemptions</h3>
          </div>
          <div className="divide-y">
            {redemptions.map(r => (
              <div key={r.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{r.reward_name}</p>
                  <p className="text-sm text-gray-500">{r.points_spent} pts • {formatDate(r.requested_at)}</p>
                </div>
                <Badge variant={r.status === 'approved' || r.status === 'fulfilled' ? 'success' : r.status === 'pending' ? 'warning' : 'danger'}>
                  {r.status}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Create Reward Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Create Reward">
        <form onSubmit={handleCreateReward} className="space-y-4">
          <Input label="Reward Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
          <Input label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required />
          <Input label="Cost (Points)" type="number" value={form.cost_points} onChange={e => setForm({ ...form, cost_points: parseInt(e.target.value) })} min={1} required />
          <Input label="Inventory (empty=unlimited)" type="number" value={form.inventory} onChange={e => setForm({ ...form, inventory: e.target.value })} min={1} />
          <Input label="Required Level" type="number" value={form.eligibility_level} onChange={e => setForm({ ...form, eligibility_level: parseInt(e.target.value) })} min={1} />
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.requires_approval} onChange={e => setForm({ ...form, requires_approval: e.target.checked })} className="rounded border-gray-300 text-primary-600" />
            <span className="text-sm">Requires admin approval</span>
          </label>
          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
            <Button type="submit" className="flex-1">Create Reward</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default RewardsPage;
